import React, { createContext, useContext, useReducer, useEffect, useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { dummyData } from '../utils/dummyData';
// import { debounce } from '../utils/formatUtils'; // Assuming debounce is in formatUtils

const API_BASE_URL = 'https://cloudspend-api.pages.dev'; // Updated to use the correct API endpoint

const ADD_TRANSACTION = 'ADD_TRANSACTION';
const DELETE_TRANSACTION = 'DELETE_TRANSACTION';
const EDIT_TRANSACTION = 'EDIT_TRANSACTION';
const ADD_CATEGORY = 'ADD_CATEGORY';
const DELETE_CATEGORY = 'DELETE_CATEGORY';
const TOGGLE_DARK_MODE = 'TOGGLE_DARK_MODE';
const LOAD_DATA = 'LOAD_DATA';
const IMPORT_DATA = 'IMPORT_DATA';
const LOAD_DEMO_DATA = 'LOAD_DEMO_DATA';
const CLEAR_DEMO_DATA = 'CLEAR_DEMO_DATA';

type Transaction = {
  id: string;
  description: string;
  amount: number;
  date: string;
  category: string;
  type: 'income' | 'expense';
  user_id: string;
  is_recurring?: boolean; // New: Indicates if the transaction is recurring
  recurrence_frequency?: string; // New: Stores the frequency of recurrence (e.g., 'daily', 'weekly', 'monthly', 'yearly')
  recurrence_start_date?: string; // New: Stores the start date of the recurrence
  recurrence_end_date?: string; // New: Stores the end date of the recurrence (optional)
};

type Category = {
  id: string;
  name: string;
  color: string;
  user_id: string;
};

type State = {
  transactions: Transaction[];
  categories: Category[];
  darkMode: boolean;
  isDemoData: boolean;
  totalTransactions: number; // New: Total count of transactions
  currentPage: number;       // New: Current page number
  itemsPerPage: number;      // New: Items per page
  budget: number;            // Monthly budget
};

type Action =
  | { type: 'ADD_TRANSACTION'; payload: Transaction }
  | { type: 'DELETE_TRANSACTION'; payload: string }
  | { type: 'EDIT_TRANSACTION'; payload: Transaction }
  | { type: 'ADD_CATEGORY'; payload: Category }
  | { type: 'DELETE_CATEGORY'; payload: string }
  | { type: 'TOGGLE_DARK_MODE' }
  | { type: 'LOAD_DATA'; payload: { transactions: Transaction[]; totalTransactions: number; currentPage: number } } // Modified payload
  | { type: 'IMPORT_DATA'; payload: Partial<State> }
  | { type: 'LOAD_DEMO_DATA' }
  | { type: 'CLEAR_DEMO_DATA' }
  | { type: 'SET_PAGE'; payload: number } // New: Set current page
  | { type: 'SET_BUDGET'; payload: number }
  | { type: 'LOAD_CATEGORIES'; payload: Category[] };

type ExpenseContextType = {
  transactions: Transaction[];
  categories: Category[];
  darkMode: boolean;
  isDemoData: boolean;
  totalIncome: number;
  totalExpenses: number;
  netBalance: number;
  categoryData: any[]; // TODO: Define type for categoryData
  monthlyData: any[]; // TODO: Define type for monthlyData
  addTransaction: (transaction: Partial<Transaction>) => Promise<void>;
  deleteTransaction: (id: string) => void;
  editTransaction: (transaction: Transaction) => void;
  addCategory: (category: Category) => void;
  deleteCategory: (id: string) => void;
  importData: (data: Partial<State>) => void;
  loadDemoData: () => void;
  clearDemoData: () => void;
  toggleDarkMode: () => void;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  totalTransactions: number; // New: Total count of transactions
  currentPage: number;       // New: Current page number
  itemsPerPage: number;      // New: Items per page
  setPage: (page: number) => void; // New: Function to change page
  budget: number;            // Monthly budget
  setBudget: (budget: number) => void; // Setter for budget
};

const initialState: State = {
  transactions: [],
  categories: [
    { id: 'food', name: 'Food', color: '#FF6B6B', user_id: '' },
    { id: 'transportation', name: 'Transportation', color: '#4ECDC4', user_id: '' },
    { id: 'entertainment', name: 'Entertainment', color: '#FFD166', user_id: '' },
    { id: 'utilities', name: 'Utilities', color: '#6B5B95', user_id: '' },
    { id: 'salary', name: 'Salary', color: '#88D498', user_id: '' },
    { id: 'freelance', name: 'Freelance', color: '#F3A712', user_id: '' },
    { id: 'other', name: 'Other', color: '#A5A58D', user_id: '' },
  ],
  darkMode: false,
  isDemoData: false,
  totalTransactions: 0, // Initialize total transactions
  currentPage: 1,        // Start on page 1
  itemsPerPage: 10,      // Default items per page
  budget: 0,             // Default monthly budget
};

  // Create context
  const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined as ExpenseContextType | undefined);

  // Define reducer function
  const expenseReducer = (state: State, action: Action): State => {
    switch (action.type) {
      case 'SET_BUDGET':
        return {
          ...state,
          budget: action.payload,
        };
      case ADD_TRANSACTION: {
        if (!action.payload.id) {
          console.error('Attempting to add transaction without ID');
          return state;
        }
        console.log('REDUCER - Adding transaction:', action.payload);
        // When adding a transaction, we might need to re-fetch or intelligently update
        // For now, just add to the current list and increment total, assuming it fits on current page
        return {
          ...state,
          transactions: [action.payload as Transaction, ...state.transactions].slice(0, state.itemsPerPage), // Add to front, keep current page size
          totalTransactions: state.totalTransactions + 1,
        };
      }
      case DELETE_TRANSACTION: {
        console.log('REDUCER - Deleting transaction with ID:', action.payload);
        const updatedTransactions = state.transactions.filter(
          (transaction) => transaction.id !== action.payload
        );
        console.log('REDUCER - Transactions after deletion:', updatedTransactions);
        return {
          ...state,
          transactions: updatedTransactions,
          totalTransactions: state.totalTransactions - 1,
        };
      }
      case EDIT_TRANSACTION: {
        if (!action.payload.id) {
          console.error('Attempting to edit transaction without ID');
          return state;
        }
        console.log('REDUCER - Editing transaction:', action.payload);
        const updatedTransactions = state.transactions.map((transaction) => {
          if (transaction.id === action.payload.id) {
            return { ...action.payload };
          }
          return transaction;
        });
        console.log('REDUCER - Transactions after edit:', updatedTransactions);
        return {
          ...state,
          transactions: updatedTransactions,
        };
      }
      case ADD_CATEGORY:
        return {
          ...state,
          categories: [...state.categories, action.payload],
        };
      case DELETE_CATEGORY:
        return {
          ...state,
          categories: state.categories.filter(
            (category) => category.id !== action.payload
          ),
        };
      case TOGGLE_DARK_MODE:
        return {
          ...state,
          darkMode: !state.darkMode,
        };
      case LOAD_DATA:
        return {
          ...state,
          transactions: action.payload.transactions.map(t => ({
            ...t,
            type: t.type as 'income' | 'expense'
          })),
          totalTransactions: action.payload.totalTransactions,
          currentPage: action.payload.currentPage,
          isDemoData: false,
        };
      case IMPORT_DATA:
        return {
          ...state,
          transactions: action.payload.transactions ? [...state.transactions, ...action.payload.transactions] : state.transactions,
          categories: action.payload.categories ? [...state.categories.filter(cat =>
            !action.payload.categories!.some(importedCat => importedCat.id === cat.id)
          ), ...action.payload.categories] : state.categories,
          totalTransactions: state.totalTransactions + (action.payload.transactions?.length || 0), // Update total
        };
      case LOAD_DEMO_DATA:
        return {
          ...state,
          transactions: dummyData.transactions.map(t => ({
            ...t,
            type: t.type as 'income' | 'expense',
            amount: Number(t.amount),
            user_id: t.user_id, // Explicitly include user_id from dummy data
          }) as Transaction), // Assert the mapped object as Transaction
          isDemoData: true,
          totalTransactions: dummyData.transactions.length,
          currentPage: 1,
        };
      case CLEAR_DEMO_DATA:
        return {
          ...state,
          transactions: [],
          isDemoData: false,
          totalTransactions: 0,
          currentPage: 1,
        };
      case 'SET_PAGE':
        return {
          ...state,
          currentPage: action.payload,
        };
      case 'LOAD_CATEGORIES':
        return {
          ...state,
          categories: action.payload,
        };
      default:
        return state;
    }
  };

  type ExpenseProviderProps = {
    children: React.ReactNode;
  addTransaction?: (transaction: Partial<Transaction>) => void;
};

// Create provider component
export const ExpenseProvider = ({ children, addTransaction: propAddTransaction }: ExpenseProviderProps) => {
  const [state, dispatch] = useReducer(expenseReducer, initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Function to clear error messages
  const clearError = () => setError(null);

  // --- Dark mode persistence and document root update ---
  // On mount, initialize darkMode from localStorage
  useEffect(() => {
    const storedDark = localStorage.getItem('expense-tracker-dark');
    if (storedDark !== null) {
      if (storedDark === 'true' && !state.darkMode) {
        dispatch({ type: TOGGLE_DARK_MODE });
      }
      if (storedDark === 'false' && state.darkMode) {
        dispatch({ type: TOGGLE_DARK_MODE });
      }
    }
    // Set document root class
    if (storedDark === 'true' || (storedDark === null && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On darkMode change, persist and update document root class
  useEffect(() => {
    localStorage.setItem('expense-tracker-dark', state.darkMode ? 'true' : 'false');
    if (state.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.darkMode]);

  // Fetch transactions from Supabase on mount and when page changes
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        // Import supabase client dynamically to avoid circular deps
        const { supabase } = await import('../utils/supabaseClient');
        
        // Get the current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // If no user, clear data and stop loading
          dispatch({ type: LOAD_DATA, payload: { transactions: [], totalTransactions: 0, currentPage: state.currentPage } });
          // Also clear categories if not authenticated
          dispatch({ type: 'LOAD_CATEGORIES', payload: [] }); // Need to add LOAD_CATEGORIES action
          setLoading(false);
          return; 
        }

        // Fetch Transactions for the current user
        const from = (state.currentPage - 1) * state.itemsPerPage;
        const to = from + state.itemsPerPage - 1;
        const { data: transactionsData, error: fetchTransactionsError, count } = await supabase
          .from('transactions')
          .select('*, is_recurring, recurrence_frequency, recurrence_start_date, recurrence_end_date', { count: 'exact' })
          .eq('user_id', user.id) // Filter by user_id
          .order('date', { ascending: false })
          .range(from, to);

        if (fetchTransactionsError) throw fetchTransactionsError;

        dispatch({
          type: LOAD_DATA,
          payload: {
            transactions: transactionsData || [],
            totalTransactions: count || 0,
            currentPage: state.currentPage,
          },
        });
        
        // Fetch Categories for the current user
        const { data: categoriesData, error: fetchCategoriesError } = await supabase
          .from('categories')
          .select('*')
          .eq('user_id', user.id); // Filter by user_id

        if (fetchCategoriesError) throw fetchCategoriesError;

        dispatch({ type: 'LOAD_CATEGORIES', payload: categoriesData || [] }); // Need to add LOAD_CATEGORIES action

      } catch (error: any) {
        console.error("Error fetching initial data:", error);
        setError(`Failed to load data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions(); // Renamed from fetchTransactions to make it clear it fetches all initial data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.itemsPerPage]);

  // Calculate totals
  const totalIncome = useMemo(() => {
    return state.transactions
      .filter((transaction) => transaction.type === 'income')
      .reduce((acc, transaction) => acc + Number(transaction.amount), 0);
  }, [state.transactions]);

  const totalExpenses = useMemo(() => {
    return state.transactions
      .filter((transaction) => transaction.type === 'expense')
      .reduce((acc, transaction) => acc + Number(transaction.amount), 0);
  }, [state.transactions]);

  const netBalance = useMemo(() => {
    return totalIncome - totalExpenses;
  }, [totalIncome, totalExpenses]);

  // Action creators
const addTransaction = async (transaction: Partial<Transaction>) => {
  setLoading(true);
  setError(null);
  try {
    const { supabase } = await import('../utils/supabaseClient');
    
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const newTransaction = {
      ...transaction,
      id: transaction.id || uuidv4(),
      amount: Number(transaction.amount),
      type: transaction.type as 'income' | 'expense',
      user_id: user.id, // Add the user_id
      date: (() => {
        // If date is empty or invalid, use current date
        if (!transaction.date || transaction.date.trim() === "") {
          return new Date().toISOString().split('T')[0];
        }
        
        // Try to parse the date
        const parsedDate = new Date(transaction.date);
        if (isNaN(parsedDate.getTime())) {
          console.warn("Invalid date provided, using current date");
          return new Date().toISOString().split('T')[0];
        }
        
        // Ensure the date is in YYYY-MM-DD format
        return parsedDate.toISOString().split('T')[0];
      })(),
      is_recurring: transaction.is_recurring || false,
      recurrence_frequency: transaction.recurrence_frequency || null,
      recurrence_start_date: transaction.recurrence_start_date && transaction.recurrence_start_date.trim() !== "" 
        ? new Date(transaction.recurrence_start_date).toISOString().split('T')[0]
        : null,
      recurrence_end_date: transaction.recurrence_end_date && transaction.recurrence_end_date.trim() !== ""
        ? new Date(transaction.recurrence_end_date).toISOString().split('T')[0]
        : null,
    } as Transaction;

    // Insert into Supabase
    console.log("Payload being sent to Supabase:", JSON.stringify({
      id: newTransaction.id,
      description: newTransaction.description,
      amount: newTransaction.amount,
      date: newTransaction.date,
      category: newTransaction.category,
      type: newTransaction.type,
      user_id: newTransaction.user_id,
      is_recurring: newTransaction.is_recurring,
      recurrence_frequency: newTransaction.recurrence_frequency,
      recurrence_start_date: newTransaction.recurrence_start_date,
      recurrence_end_date: newTransaction.recurrence_end_date,
    }, null, 2));

    const { data, error: insertError } = await supabase
      .from('transactions')
      .insert([{
        id: newTransaction.id,
        description: newTransaction.description,
        amount: newTransaction.amount,
        date: newTransaction.date,
        category: newTransaction.category,
        type: newTransaction.type,
        user_id: newTransaction.user_id,
        is_recurring: newTransaction.is_recurring,
        recurrence_frequency: newTransaction.recurrence_frequency,
        recurrence_start_date: newTransaction.recurrence_start_date,
        recurrence_end_date: newTransaction.recurrence_end_date,
      }])
      .select()
      .single();

    if (insertError) throw insertError;

    dispatch({
      type: ADD_TRANSACTION,
      payload: data,
    });
    console.log("Transaction added successfully!");
  } catch (error: any) {
    console.error("Error adding transaction:", error);
    setError(`Failed to add transaction: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  const deleteTransaction = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { supabase } = await import('../utils/supabaseClient');
      const { error: deleteError } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      dispatch({
        type: DELETE_TRANSACTION,
        payload: id,
      });
      console.log("Transaction deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting transaction:", error);
      setError(`Failed to delete transaction: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const editTransaction = async (transaction: Transaction) => {
    setLoading(true);
    setError(null);
    try {
      const updatedTransaction = {
        ...transaction,
        amount: parseFloat(String(transaction.amount)),
      };

      const { supabase } = await import('../utils/supabaseClient');
      const { data, error: updateError } = await supabase
        .from('transactions')
        .update(updatedTransaction)
        .eq('id', transaction.id)
        .select()
        .single();

      if (updateError) throw updateError;

      dispatch({
        type: EDIT_TRANSACTION,
        payload: data,
      });
      console.log("Transaction updated successfully!");
    } catch (error: any) {
      console.error("Error editing transaction:", error);
      setError(`Failed to edit transaction: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addCategory = async (category: Partial<Category>) => {
    setLoading(true);
    setError(null);
    try {
      const { supabase } = await import('../utils/supabaseClient');

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const newCategory = {
        ...category,
        // Generate ID from name if name exists, otherwise use a new UUID
        id: category.id || (category.name ? category.name.toLowerCase().replace(/\s+/g, '_') : uuidv4()),
        user_id: user.id, // Add the user_id
        // Ensure name and color have fallback values if not provided
        name: category.name || 'Unnamed Category',
        color: category.color || '#000000',
      } as Category; // Assert type to include user_id, name, and color

      const { data, error: insertError } = await supabase
        .from('categories')
        .insert([newCategory])
        .select()
        .single();

      if (insertError) throw insertError;

      dispatch({
        type: ADD_CATEGORY,
        payload: data,
      });
      console.log("Category added successfully!");
    } catch (error: any) {
      console.error("Error adding category:", error);
      setError(`Failed to add category: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { supabase } = await import('../utils/supabaseClient');
      const { error: deleteError } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      dispatch({
        type: DELETE_CATEGORY,
        payload: id,
      });
      console.log("Category deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting category:", error);
      setError(`Failed to delete category: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const importData = (data: Partial<State>) => {
    setLoading(true);
    setError(null);
    try {
      // Ensure all imported transactions have IDs
      if (data.transactions) {
        data.transactions = data.transactions.map(transaction => {
          const validatedTransaction: Transaction = {
            id: transaction.id || uuidv4(), // Use existing ID or generate new
            description: transaction.description || 'Imported Transaction',
            amount: Number(transaction.amount) || 0,
            date: transaction.date || new Date().toISOString().split('T')[0],
            category: transaction.category || 'other',
            type: transaction.type === 'income' || transaction.type === 'expense' ? transaction.type : 'expense', // Default to expense if type is invalid
            user_id: transaction.user_id || '', // Use existing user_id or default to empty string
          };
          return validatedTransaction;
        });
      }

      dispatch({
        type: IMPORT_DATA,
        payload: data,
      });
      console.log("Data imported successfully!"); // Success feedback
    } catch (error: any) {
      console.error("Error importing data:", error);
      setError(`Failed to import data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDemoData = () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Loading demo data");
      dispatch({ type: LOAD_DEMO_DATA });
      console.log("Demo data loaded successfully!"); // Success feedback
    } catch (error: any) {
      console.error("Error loading demo data:", error);
      setError(`Failed to load demo data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearDemoData = () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Clearing demo data");
      dispatch({ type: CLEAR_DEMO_DATA });
      console.log("Demo data cleared successfully!"); // Success feedback
    } catch (error: any) {
      console.error("Error clearing demo data:", error);
      setError(`Failed to clear demo data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleDarkMode = () => {
    setLoading(true);
    setError(null);
    try {
      dispatch({ type: TOGGLE_DARK_MODE });
      // localStorage and document root class are handled by useEffect above
      console.log("Dark mode toggled!"); // Success feedback
    } catch (error: any) {
      console.error("Error toggling dark mode:", error);
      setError(`Failed to toggle dark mode: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Group expenses by category
  const expensesByCategory = useMemo(() => {
    return state.transactions
      .filter((transaction) => transaction.type === 'expense')
      .reduce((categories, transaction) => {
        const category = transaction.category;
        if (!categories[category]) {
          categories[category] = 0;
        }
        categories[category] += Number(transaction.amount);
        return categories;
      }, {});
  }, [state.transactions, state.categories]);

  // Format category data for charts
  const categoryData = useMemo(() => {
    return Object.entries(expensesByCategory).map(
      ([categoryId, amount]) => {
        const category = state.categories.find((cat) => cat.id === categoryId) || {
          name: categoryId,
          color: '#888',
        };
        const amt = Number(amount) || 0;
        return {
          id: categoryId,
          name: category.name,
          amount: amt,
          percentage: totalExpenses > 0 ? (amt / totalExpenses) * 100 : 0,
          color: category.color,
        };
      }
    );
  }, [expensesByCategory, state.categories, totalExpenses]);

  // Group transactions by month
  const monthlyData = useMemo(() => {
    const months: { [key: string]: { income: number; expense: number; label: string; monthKey: string } } = {};

    state.transactions.forEach((transaction) => {
      try {
        const date = transaction.date ? new Date(transaction.date) : new Date();
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`; // YYYY-MM for reliable sorting

        if (!months[monthKey]) {
          months[monthKey] = {
            income: 0,
            expense: 0,
            label: new Date(date.getFullYear(), date.getMonth(), 1)
              .toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            monthKey: monthKey,
          };
        }

        if (transaction.type === 'income') {
          months[monthKey].income += Number(transaction.amount);
        } else {
          months[monthKey].expense += Number(transaction.amount);
        }
      } catch (error) {
        console.error("Error processing transaction for monthly data:", transaction, error);
      }
    });

    return Object.values(months).sort((a, b) =>
      a.monthKey.localeCompare(b.monthKey) // Sort by YYYY-MM string
    );
  }, [state.transactions]);

  // Budget setter
  const setBudget = (budget: number) => {
    dispatch({ type: 'SET_BUDGET', payload: budget });
  };

  return (
    <ExpenseContext.Provider
      value={{
        transactions: state.transactions,
        categories: state.categories,
        darkMode: state.darkMode,
        isDemoData: state.isDemoData,
        totalIncome,
        totalExpenses,
        netBalance,
        categoryData,
        monthlyData,
        addTransaction,
        deleteTransaction,
        editTransaction,
        addCategory,
        deleteCategory,
        importData,
        loadDemoData,
        clearDemoData,
        toggleDarkMode,
        loading,
        error,
        clearError,
        totalTransactions: state.totalTransactions,
        currentPage: state.currentPage,
        itemsPerPage: state.itemsPerPage,
        setPage: (page: number) => dispatch({ type: 'SET_PAGE', payload: page }),
        budget: state.budget,
        setBudget,
      }}
    >
      {children}
    </ExpenseContext.Provider>
  );
};

// Create custom hook for using the expense context
export const useExpenseContext = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpenseContext must be used within an ExpenseProvider');
  }
  return context;
};
